import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import slugify from "slugify";
import EmbedCode from "./embedCode";

const useResolveStrapiBlogText = (markdownText) => {
  const headingRegex = /^## (.+)$/gm;
  const matches = [...markdownText.matchAll(headingRegex)];
  const headers = matches.map((match) => {
    const text = match[1];
    const id = slugify(text, { lower: true, strict: true });
    return { id, text };
  });

  function parseTextWithBoldAndLinks(children, keyPrefix = "") {
    const childArray = React.Children.toArray(children);

    return childArray.flatMap((child, index) => {
      if (typeof child === "string") {
        const parts = child
          .split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g) // **bold** or [link](url)
          .filter(Boolean);

        return parts.map((part, i) => {
          if (part.startsWith("**") && part.endsWith("**")) {
            return (
              <b key={`${keyPrefix}-${index}-${i}`}>{part.slice(2, -2)}</b>
            );
          }
          if (
            part.startsWith("[") &&
            part.includes("](") &&
            part.endsWith(")")
          ) {
            const match = part.match(/\[([^\]]+)\]\(([^)]+)\)/);
            if (match) {
              const [_, text, href] = match;
              return (
                <a
                  key={`${keyPrefix}-${index}-${i}`}
                  href={href}
                  className="text-link text-decoration-underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {text}
                </a>
              );
            }
          }
          return part;
        });
      } else {
        return child;
      }
    });
  }

  const renderers = {
    h1({ children }) {
      return <h1>{children}</h1>;
    },
    h2({ children }) {
      const text = children || "";
      const id = slugify(text, { lower: true, strict: true });
      return (
        <h2
          id={id}
          className="text-h2 gap-4x topic-title text-fw-medium right-el"
        >
          {text}
        </h2>
      );
    },
    h3({ children }) {
      return (
        <h3 className="text-h3 gap-2x text-clr-primary text-fw-medium">
          {children}
        </h3>
      );
    },
    h4({ children }) {
      return (
        <h4 className="text-h4 gap-1x text-clr-primary text-fw-medium">
          {children}
        </h4>
      );
    },
    h5({ children }) {
      return <h5 className=" text-clr-primary text-fw-medium">{children}</h5>;
    },
    h6({ children }) {
      return <h6 className=" text-clr-primary text-fw-medium">{children}</h6>;
    },
    p({ children }) {
      const html = React.Children.toArray(children).map(String).join("");

      if (html.includes("<iframe") || html.includes("twitter-tweet")) {
        return <EmbedCode htmlContent={html} />;
      }

      const parsedChildren = parseTextWithBoldAndLinks(children, "p");

      return (
        <p className="text-p1 text-fw-regular text-clr-secondary gap-2x">
          {parsedChildren}
        </p>
      );
    },

    blockquote({ children }) {
      const childArray = React.Children.toArray(children);

      const isTwitterEmbed = childArray.some((child) => {
        if (typeof child === "string") {
          return false;
        }
        if (React.isValidElement(child) && child.type === "a") {
          const href = child.props.href;
          return href && href.includes("twitter.com");
        }
        return false;
      });

      function extractText(node) {
        if (typeof node === "string") {
          return node;
        } else if (Array.isArray(node)) {
          return node.map(extractText).join(""); // recursively extract array children
        } else if (React.isValidElement(node)) {
          return extractText(node.props.children); // recurse into props.children
        }
        return "";
      }

      const textContent = React.Children.toArray(children)
        .map(extractText)
        .join("");

      if (isTwitterEmbed) {
        return (
          <blockquote
            className="twitter-tweet"
            dangerouslySetInnerHTML={{ __html: textContent }}
          />
        );
      }

      const paragraphs = textContent
        .split("\n")
        .filter((line) => line.trim() !== "");

      return (
        <blockquote className="blog-blockquote">
          {paragraphs.map((paragraph, index) => (
            <p
              key={index}
              className={`text-p1 font--primary text-fw-medium text-clr-white blog-paragraph ${
                index === 0
                  ? "first-paragraph"
                  : index === paragraphs.length - 1
                  ? "last-paragraph"
                  : "middle-paragraph"
              }`}
            >
              {paragraph.trim()}
            </p>
          ))}
        </blockquote>
      );
    },

    table({ children }) {
      return (
        <div className="rich-text-table">
          <table>
            <tbody>{children}</tbody>
          </table>
        </div>
      );
    },
    thead({ children }) {
      return <thead>{children}</thead>;
    },
    tbody({ children }) {
      return <tbody>{children}</tbody>;
    },
    tr({ children }) {
      return <tr>{children}</tr>;
    },
    td({ children }) {
      const parsedChildren = parseTextWithBoldAndLinks(children, "td");

      return (
        <td>
          <p className="text-p1 text-fw-regular text-clr-secondary gap-2x">
            {parsedChildren}
          </p>
        </td>
      );
    },
    th({ children }) {
      return (
        <th>
          <p class="text-p1 text-fw-strong text-clr-secondary gap-2x">
            {children}
          </p>
        </th>
      );
    },

    code({ node, inline, className, children, ...props }) {
      return inline ? (
        <pre className="code-block" {...props}>
          {children}
        </pre>
      ) : (
        <pre className="code-block">
          <code {...props}>{children}</code>
        </pre>
      );
    },

    img({ src, alt }) {
      if (!src) return null;

      const width = 1380;
      //   const height = 775;
      const height = 675;
      const baseSrc = src.split("?")[0];

      const webpSrcSet = [
        `${baseSrc}?w=345&h=194&q=100&fm=webp 345w`,
        `${baseSrc}?w=690&h=388&q=100&fm=webp 690w`,
        `${baseSrc}?w=1380&h=775&q=100&fm=webp 1380w`,
      ].join(", ");

      const pngSrcSet = [
        `${baseSrc}?w=345&h=194&q=100&fm=png 345w`,
        `${baseSrc}?w=690&h=388&q=100&fm=png 690w`,
        `${baseSrc}?w=1380&h=775&q=100&fm=png 1380w`,
      ].join(", ");

      const sizes = "(min-width: 1380px) 1380px, 100vw";

      const placeholderSvg = `data:image/svg+xml;charset=utf-8,<svg height='${height}' width='${width}' xmlns='http://www.w3.org/2000/svg' version='1.1'></svg>`;
      const placeholderBase64 =
        "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAALCAAAAABaYvKfAAAAcUlEQVQIHW3BORKDMBAAwf3/7/ARkRAgXBwWhW3BIqEdcK5uoUDADv6ScckGCMSPAclHLl8FxNa0Lxsa83agmtcxIjY55zxLO/YzOg5TFxB7N9VrzkP9qFvrn9Xt3iCkMGiCHDLswXc/RSgQCoQCoeAEnirWjGYKykgAAAAASUVORK5CYII=";

      return (
        <div
          data-gatsby-image-wrapper=""
          className="gatsby-image-wrapper gatsby-image-wrapper-constrained gap-3x"
        >
          <div style={{ maxWidth: `${width}px`, display: "block" }}>
            <img
              alt=""
              role="presentation"
              aria-hidden="true"
              src={placeholderSvg}
              style={{ maxWidth: "100%", display: "block", position: "static" }}
            />
          </div>
          <img
            aria-hidden="true"
            data-placeholder-image=""
            style={{
              opacity: 0,
              transition: "opacity 500ms linear",
            }}
            decoding="async"
            src={placeholderBase64}
            alt=""
          />
          <picture>
            <source type="image/webp" sizes={sizes} srcSet={webpSrcSet} />
            <img
              data-main-image=""
              style={{ opacity: 1 }}
              sizes={sizes}
              decoding="async"
              loading="lazy"
              alt={alt || ""}
              src={`${baseSrc}?w=${width}&h=${height}&q=100&fm=png`}
              srcSet={pngSrcSet}
            />
          </picture>
        </div>
      );
    },

    li({ children }) {
      const parsedChildren = parseTextWithBoldAndLinks(children, "li");
      return (
        <li>
          <p className="text-p1 text-fw-regular text-clr-primary gap-2x">
            {parsedChildren}
          </p>
        </li>
      );
    },
  };

  const markdownElement = (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeRaw]}
      components={renderers}
    >
      {markdownText}
    </ReactMarkdown>
  );

  return buildLayout(markdownElement, headers);
};

function buildLayout(content, headers) {
  const leftNav = headers.map((h) => (
    <a
      key={h.id}
      href={`#${h.id}`}
      className="chapter text-p1 text-fw-regular text-clr-primary"
    >
      {h.text}
    </a>
  ));

  return (
    <div className="blog-detail-content__row">
      <div className="blog-detail-content__row-left">
        <div className="wrapper">
          <div className="blog-row__left-header gap-3x text-h3 text-clr-primary text-fw-medium">
            Chapters
          </div>
          <div className="title-wrap">{leftNav}</div>
        </div>
      </div>
      <div className="blog-detail-content__row-right">{content}</div>
    </div>
  );
}

export default useResolveStrapiBlogText;
